import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";

import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
  ApiError,
  defaultError,
} from "../../lib/redux";
import {
  AddPickupInstructionsParams,
  CancelInvoiceParams,
  CreateCustomInvoiceParams,
  CreateWeightOverageInvoiceParams,
  GetInvoicesParams,
  OrderFilters,
  ResendInvoiceParams,
  SendConfirmationEmailParams,
  TriplePPagination,
  triplePSDatasource,
  UpdateOrderFormModel,
  UpdatePaymentTypeParams,
} from "../../datasource";
import {
  DashboardOrderStats,
  Invoice,
  Order,
  OrderDetailsInfo,
  OrderProfit,
  OrderStatus,
  PickUp,
} from "../../models";
import { createOrderAPI } from "../CreateOrder/createOrderSlice";
import { cancelOrderAPI } from "../CancelOrder/cancelOrderSlice";
import { logout } from "../Auth/authSlice";

export interface OrdersState {
  getOrders: APIResult<Order[]>;
  getOrder: APIResult<Order>;
  getInvoice: APIResult<string>;
  getDashboardOrderStats: APIResult<DashboardOrderStats>;
  updateOrder: APIResult<Order>;
  getPickupInstructionsAPI: APIResult<PickUp>;
  addPickupInstructionsAPI: APIResult<string>;
  sendConfirmationEmailAPI: APIResult<string>;
  getInvoicesAPI: APIResult<Invoice[]>;
  getInvoicePDFAPI: APIResult<string>;
  createWeightOverageInvoiceAPI: APIResult<boolean>;
  createCustomInvoiceAPI: APIResult<boolean>;
  cancelInvoiceAPI: APIResult<boolean>;
  resendInvoiceAPI: APIResult<boolean>;
  updatePaymentTypeAPI: APIResult<Order>;
  getOrderDetailsAPI: APIResult<OrderDetailsInfo>;
  getOrderProfitAPI: APIResult<OrderProfit>;

  orders: Order[];
  invoice?: string;
  dashboardOrderStats?: DashboardOrderStats;
  showUpdateOrderModal?: Order;
  showDeleteOrderModal?: string;
  showOrderDetailsModal?: string;
  pickupInstructions?: string;
  showPickupInstructionsModal?: string;
  signature?: string;
  order?: Order;
  orderDetails?: OrderDetailsInfo;
  orderProfit?: OrderProfit;
  invoices: Invoice[];
  invoicePDF?: string;
  showAddInvoiceModal?: Order;
  showUpdatePaymentTypeModal?: Order;
}

const initialState: OrdersState = {
  getOrders: emptyAPIResult(),
  getOrder: emptyAPIResult(),
  getInvoice: emptyAPIResult(),
  getDashboardOrderStats: emptyAPIResult(),
  updateOrder: emptyAPIResult(),
  getPickupInstructionsAPI: emptyAPIResult(),
  addPickupInstructionsAPI: emptyAPIResult(),
  sendConfirmationEmailAPI: emptyAPIResult(),
  getInvoicesAPI: emptyAPIResult(),
  getInvoicePDFAPI: emptyAPIResult(),
  createWeightOverageInvoiceAPI: emptyAPIResult(),
  createCustomInvoiceAPI: emptyAPIResult(),
  cancelInvoiceAPI: emptyAPIResult(),
  resendInvoiceAPI: emptyAPIResult(),
  updatePaymentTypeAPI: emptyAPIResult(),
  getOrderDetailsAPI: emptyAPIResult(),
  getOrderProfitAPI: emptyAPIResult(),

  orders: [],
  invoices: [],
};

export const getOrders = createAsyncThunk(
  "orders/getOrders",
  async (data: { pagination: TriplePPagination; filters: OrderFilters }) => {
    return triplePSDatasource.getOrders(data.filters, data.pagination);
  }
);

export const getOrder = createAsyncThunk(
  "orders/getOrder",
  async (id: string) => {
    return triplePSDatasource.getOrder(id);
  }
);

export const getOrderDetails = createAsyncThunk(
  "orders/getOrderDetails",
  async (id: string) => {
    return triplePSDatasource.getOrderDetails(id);
  }
);

export const getOrderProfit = createAsyncThunk(
  "orders/getOrderProfit",
  async (id: string) => {
    return triplePSDatasource.getOrderProfit(id);
  }
);

export const updateOrder = createAsyncThunk<
  Order,
  UpdateOrderFormModel,
  { rejectValue: ApiError }
>("orders/updateOrder", async (form, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.updateOrder(form);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getInvoice = createAsyncThunk(
  "orders/getInvoice",
  async (id: string) => {
    return triplePSDatasource.getInvoice(id);
  }
);

export const getDashboardOrderStats = createAsyncThunk(
  "orders/getDashboardOrderStats",
  async () => {
    return triplePSDatasource.getOrderStats();
  }
);

export const getPickupInstructions = createAsyncThunk<
  PickUp,
  string,
  { rejectValue: ApiError }
>("orders/getPickupInstructions", async (id, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getPickupInstructions(id);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const addPickupInstructions = createAsyncThunk<
  string,
  AddPickupInstructionsParams,
  { rejectValue: ApiError }
>(
  "orders/addPickupInstructions",
  async (params: AddPickupInstructionsParams, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.addPickupInstructions(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const sendConfirmationEmail = createAsyncThunk<
  string,
  SendConfirmationEmailParams,
  { rejectValue: ApiError }
>(
  "orders/sendConfirmationEmail",
  async (params: SendConfirmationEmailParams, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.sendConfirmationEmail(params);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const getInvoices = createAsyncThunk<
  Invoice[],
  GetInvoicesParams,
  { rejectValue: ApiError }
>("orders/getInvoices", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.getInvoices(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const getInvoicePDF = createAsyncThunk<
  string,
  any,
  { rejectValue: ApiError }
>(
  "orders/getInvoicePDF",
  async (params: { id: string }, { rejectWithValue }) => {
    try {
      const data = await triplePSDatasource.getInvoicePDF(params.id);

      return data;
    } catch (err: any) {
      const error = err.response.data?.errors?.[0] || defaultError;

      return rejectWithValue(error);
    }
  }
);

export const createWeightOverageInvoice = createAsyncThunk<
  boolean,
  CreateWeightOverageInvoiceParams,
  { rejectValue: ApiError }
>("orders/createWeightOverageInvoice", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.createWeightOverageInvoice(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const createCustomInvoice = createAsyncThunk<
  boolean,
  CreateCustomInvoiceParams,
  { rejectValue: ApiError }
>("orders/createCustomInvoice", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.createCustomInvoice(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const cancelInvoice = createAsyncThunk<
  boolean,
  CancelInvoiceParams,
  { rejectValue: ApiError }
>("orders/cancelInvoice", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.cancelInvoice(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const resendInvoice = createAsyncThunk<
  boolean,
  ResendInvoiceParams,
  { rejectValue: ApiError }
>("orders/resendInvoice", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.resendInvoice(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const updatePaymentType = createAsyncThunk<
  Order,
  UpdatePaymentTypeParams,
  { rejectValue: ApiError }
>("orders/updatePaymentType", async (params, { rejectWithValue }) => {
  try {
    const data = await triplePSDatasource.updatePaymentType(params);

    return data;
  } catch (err: any) {
    const error = err.response.data?.errors?.[0] || defaultError;

    return rejectWithValue(error);
  }
});

export const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    resetInvoice(state) {
      state.invoice = initialState.invoice;
      state.getInvoice = initialState.getInvoice;
    },
    refreshOrders(state) {
      state.getOrders = initialState.getOrders;
      state.orders = [];
    },
    setShowUpdateOrderModal(state, action) {
      state.showUpdateOrderModal = action.payload;
    },
    setShowDeleteOrderModal(state, action) {
      state.showDeleteOrderModal = action.payload;
    },
    setShowOrderDetailsModal(state, action) {
      state.showOrderDetailsModal = action.payload;
    },
    setShowPickupInstructionsModal(state, action) {
      state.showPickupInstructionsModal = action.payload;
    },
    updateSignature(state, action) {
      state.signature = action.payload;
    },
    resetGetPickupInstructions(state) {
      state.getPickupInstructionsAPI = initialState.getPickupInstructionsAPI;
      state.pickupInstructions = undefined;
    },
    resetAddPickupInstructions(state) {
      state.addPickupInstructionsAPI = initialState.addPickupInstructionsAPI;
    },
    resetSendEmailConfirmation(state) {
      state.sendConfirmationEmailAPI = initialState.sendConfirmationEmailAPI;
    },
    resetInvoices(state) {
      state.getInvoicesAPI = initialState.getInvoicesAPI;
      state.invoices = [];
    },
    resetOrder(state) {
      state.getOrder = initialState.getOrder;
      state.order = undefined;
      state.getOrderDetailsAPI = initialState.getOrderDetailsAPI;
      state.orderDetails = undefined;
      state.getOrderProfitAPI = initialState.getOrderProfitAPI;
      state.orderProfit = undefined;
    },
    resetInvoicePDF(state) {
      state.getInvoicePDFAPI = initialState.getInvoicePDFAPI;
      state.invoicePDF = undefined;
    },
    setShowAddInvoiceModal(state, action) {
      state.showAddInvoiceModal = action.payload;
    },
    resetCreateWeighOverageInvoice(state) {
      state.createWeightOverageInvoiceAPI =
        initialState.createWeightOverageInvoiceAPI;
    },
    resetCreateCustomInvoice(state) {
      state.createCustomInvoiceAPI = initialState.createCustomInvoiceAPI;
    },
    resetCancelInvoice(state) {
      state.cancelInvoiceAPI = initialState.cancelInvoiceAPI;
    },
    resetResendInvoice(state) {
      state.resendInvoiceAPI = initialState.resendInvoiceAPI;
    },
    setShowUpdatePaymentTypeModal(state, action) {
      state.showUpdatePaymentTypeModal = action.payload;
    },
    resetUpdatePaymentType(state) {
      state.updatePaymentTypeAPI = initialState.updatePaymentTypeAPI;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrders.pending, (state) => {
        state.getOrders = loadingAPIResult();
      })
      .addCase(getOrders.fulfilled, (state, action) => {
        state.getOrders = successAPIResult(action.payload);

        if (action.meta.arg.pagination.offset === 0) {
          state.orders = action.payload;
        } else {
          state.orders.push(...action.payload);
        }
      })
      .addCase(getOrders.rejected, (state, action) => {
        state.getOrders = errorAPIResult(action.error);
      })
      .addCase(getOrder.pending, (state) => {
        state.getOrder = loadingAPIResult();
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.getOrder = successAPIResult(action.payload);
        state.orders.push(action.payload);
        state.order = action.payload;
      })
      .addCase(getOrder.rejected, (state, action) => {
        state.getOrder = errorAPIResult(action.error);
      })
      .addCase(getOrderDetails.pending, (state) => {
        state.getOrderDetailsAPI = loadingAPIResult();
      })
      .addCase(getOrderDetails.fulfilled, (state, action) => {
        state.getOrderDetailsAPI = successAPIResult(action.payload);
        state.orderDetails = action.payload;
      })
      .addCase(getOrderDetails.rejected, (state, action) => {
        state.getOrderDetailsAPI = errorAPIResult(action.error);
      })
      .addCase(getOrderProfit.pending, (state) => {
        state.getOrderProfitAPI = loadingAPIResult();
      })
      .addCase(getOrderProfit.fulfilled, (state, action) => {
        state.getOrderProfitAPI = successAPIResult(action.payload);
        state.orderProfit = action.payload;
      })
      .addCase(getOrderProfit.rejected, (state, action) => {
        state.getOrderProfitAPI = errorAPIResult(action.error);
      })
      .addCase(getInvoice.pending, (state) => {
        state.getInvoice = loadingAPIResult();
      })
      .addCase(getInvoice.fulfilled, (state, action) => {
        state.getInvoice = successAPIResult(action.payload);
        state.invoice = action.payload;
      })
      .addCase(getInvoice.rejected, (state, action) => {
        state.getInvoice = errorAPIResult(action.error);
      })
      .addCase(getDashboardOrderStats.pending, (state) => {
        state.getDashboardOrderStats = loadingAPIResult();
      })
      .addCase(getDashboardOrderStats.fulfilled, (state, action) => {
        state.getDashboardOrderStats = successAPIResult(action.payload);
        state.dashboardOrderStats = action.payload;
      })
      .addCase(getDashboardOrderStats.rejected, (state, action) => {
        state.getDashboardOrderStats = errorAPIResult(action.error);
      })
      .addCase(updateOrder.pending, (state) => {
        state.updateOrder = loadingAPIResult();
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.updateOrder = successAPIResult(action.payload);
        const newOrders = state.orders.map((o) => {
          return o.id === action.payload.id ? action.payload : o;
        });
        state.orders = newOrders;
        // Keep the open detail view in sync so edits (e.g. linking a customer)
        // show in place, without a refetch that would flash the loading state.
        if (state.order && state.order.id === action.payload.id) {
          state.order = action.payload;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.updateOrder = errorAPIResult(action.payload!);
      })
      .addCase(getPickupInstructions.pending, (state) => {
        state.getPickupInstructionsAPI = loadingAPIResult();
      })
      .addCase(getPickupInstructions.fulfilled, (state, action) => {
        state.getPickupInstructionsAPI = successAPIResult(action.payload);
        state.pickupInstructions = action.payload.instructions;
      })
      .addCase(getPickupInstructions.rejected, (state, action) => {
        state.getPickupInstructionsAPI = errorAPIResult(action.payload!);
      })
      .addCase(addPickupInstructions.pending, (state) => {
        state.addPickupInstructionsAPI = loadingAPIResult();
      })
      .addCase(addPickupInstructions.fulfilled, (state, action) => {
        state.addPickupInstructionsAPI = successAPIResult(action.payload);
      })
      .addCase(addPickupInstructions.rejected, (state, action) => {
        state.addPickupInstructionsAPI = errorAPIResult(action.payload!);
      })
      .addCase(sendConfirmationEmail.pending, (state) => {
        state.sendConfirmationEmailAPI = loadingAPIResult();
      })
      .addCase(sendConfirmationEmail.fulfilled, (state, action) => {
        state.sendConfirmationEmailAPI = successAPIResult(action.payload);
      })
      .addCase(sendConfirmationEmail.rejected, (state, action) => {
        state.sendConfirmationEmailAPI = errorAPIResult(action.payload!);
      })
      .addCase(getInvoices.pending, (state) => {
        state.getInvoicesAPI = loadingAPIResult();
      })
      .addCase(getInvoices.fulfilled, (state, action) => {
        state.getInvoicesAPI = successAPIResult(action.payload);
        state.invoices = action.payload;
      })
      .addCase(getInvoices.rejected, (state, action) => {
        state.getInvoicesAPI = errorAPIResult(action.error);
      })
      .addCase(getInvoicePDF.pending, (state) => {
        state.getInvoicePDFAPI = loadingAPIResult();
      })
      .addCase(getInvoicePDF.fulfilled, (state, action) => {
        state.getInvoicePDFAPI = successAPIResult(action.payload);
        state.invoicePDF = action.payload;
      })
      .addCase(getInvoicePDF.rejected, (state, action) => {
        state.getInvoicePDFAPI = errorAPIResult(action.payload!);
      })
      .addCase(createWeightOverageInvoice.pending, (state) => {
        state.createWeightOverageInvoiceAPI = loadingAPIResult();
      })
      .addCase(createWeightOverageInvoice.fulfilled, (state, action) => {
        state.createWeightOverageInvoiceAPI = successAPIResult(action.payload);
      })
      .addCase(createWeightOverageInvoice.rejected, (state, action) => {
        state.createWeightOverageInvoiceAPI = errorAPIResult(action.payload!);
      })
      .addCase(createCustomInvoice.pending, (state) => {
        state.createCustomInvoiceAPI = loadingAPIResult();
      })
      .addCase(createCustomInvoice.fulfilled, (state, action) => {
        state.createCustomInvoiceAPI = successAPIResult(action.payload);
      })
      .addCase(createCustomInvoice.rejected, (state, action) => {
        state.createCustomInvoiceAPI = errorAPIResult(action.payload!);
      })
      .addCase(cancelInvoice.pending, (state) => {
        state.cancelInvoiceAPI = loadingAPIResult();
      })
      .addCase(cancelInvoice.fulfilled, (state, action) => {
        state.cancelInvoiceAPI = successAPIResult(action.payload);
      })
      .addCase(cancelInvoice.rejected, (state, action) => {
        state.cancelInvoiceAPI = errorAPIResult(action.payload!);
      })
      .addCase(resendInvoice.pending, (state) => {
        state.resendInvoiceAPI = loadingAPIResult();
      })
      .addCase(resendInvoice.fulfilled, (state, action) => {
        state.resendInvoiceAPI = successAPIResult(action.payload);
      })
      .addCase(resendInvoice.rejected, (state, action) => {
        state.resendInvoiceAPI = errorAPIResult(action.payload!);
      })
      .addCase(updatePaymentType.pending, (state) => {
        state.updatePaymentTypeAPI = loadingAPIResult();
      })
      .addCase(updatePaymentType.fulfilled, (state, action) => {
        state.updatePaymentTypeAPI = successAPIResult(action.payload);
        state.order = action.payload;
        const newOrders = state.orders.map((o) => {
          return o.id === action.payload.id ? action.payload : o;
        });
        state.orders = newOrders;
      })
      .addCase(updatePaymentType.rejected, (state, action) => {
        state.updatePaymentTypeAPI = errorAPIResult(action.payload!);
      })
      .addMatcher(isAnyOf(createOrderAPI.fulfilled), (state, action) => {
        state.orders.push(action.payload);
        state.signature = undefined;
      })
      .addMatcher(isAnyOf(cancelOrderAPI.fulfilled), (state, action) => {
        const order = state.orders.find((o) => o.id === action.payload);
        if (!!order) {
          order.status = OrderStatus.OrderStatusCanceled;
        }
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getOrders = initialState.getOrders;
        state.getOrder = initialState.getOrder;
        state.getInvoice = initialState.getInvoice;
        state.getDashboardOrderStats = initialState.getDashboardOrderStats;
        state.updateOrder = initialState.updateOrder;
        state.getPickupInstructionsAPI = initialState.getPickupInstructionsAPI;
        state.addPickupInstructionsAPI = initialState.addPickupInstructionsAPI;
        state.sendConfirmationEmailAPI = initialState.sendConfirmationEmailAPI;

        state.orders = initialState.orders;
        state.invoice = undefined;
        state.dashboardOrderStats = undefined;
        state.showUpdateOrderModal = undefined;
        state.showDeleteOrderModal = undefined;
        state.showOrderDetailsModal = undefined;
        state.pickupInstructions = undefined;
        state.showPickupInstructionsModal = undefined;
        state.signature = undefined;
      });
  },
});

export const {
  resetInvoice,
  refreshOrders,
  setShowUpdateOrderModal,
  setShowDeleteOrderModal,
  setShowOrderDetailsModal,
  updateSignature,
  setShowPickupInstructionsModal,
  resetGetPickupInstructions,
  resetAddPickupInstructions,
  resetSendEmailConfirmation,
  resetInvoices,
  resetOrder,
  resetInvoicePDF,
  setShowAddInvoiceModal,
  resetCreateWeighOverageInvoice,
  resetCreateCustomInvoice,
  resetCancelInvoice,
  resetResendInvoice,
  setShowUpdatePaymentTypeModal,
  resetUpdatePaymentType,
} = ordersSlice.actions;
export default ordersSlice.reducer;
